import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

// Configurable trial period (in days) - set to 0 for no trial
export const TRIAL_PERIOD_DAYS = 0;

// Tier definitions with feature access
export const TIER_FEATURES = {
    scout: {
        name: 'Scout',
        icon: '🔍',
        level: 0,
        features: [
            'menu-management',
            'pos',
            'kitchen',
            'server',
            'orders',
            'payments'
        ]
    },
    ally: {
        name: 'Ally',
        icon: '🌱',
        level: 1,
        features: [
            'menu-management',
            'pos',
            'kitchen',
            'server',
            'orders',
            'payments'
            // Analytics moved to Guide tier
        ]
    },
    guide: {
        name: 'Guide',
        icon: '🧭',
        level: 2,
        features: [
            'menu-management',
            'pos',
            'kitchen',
            'server',
            'orders',
            'payments',
            'analytics',
            'website-builder',
            'website-integration'
        ]
    },
    chief: {
        name: 'Chief',
        icon: '🦅',
        level: 3,
        popular: true, // Most popular tier
        features: [
            'menu-management',
            'pos',
            'kitchen',
            'server',
            'orders',
            'payments',
            'analytics',
            'website-builder',
            'website-integration',
            'seo-social', // Without AI features
            'delivery'
        ],
        // AI features visible but disabled
        previewFeatures: ['ai-analytics', 'ai-content']
    },
    elder: {
        name: 'Elder',
        icon: '👑',
        level: 4,
        features: [
            'menu-management',
            'pos',
            'kitchen',
            'server',
            'orders',
            'payments',
            'analytics',
            'website-builder',
            'website-integration',
            'seo-social',
            'ai-analytics',
            'ai-content',
            'delivery',
            'mobile-app'
        ],
        // Coming soon features to entice upgrades
        comingSoon: [
            'inventory-tracking',
            'ai-forecasting',
            'supplier-management',
            'smart-scheduling',
            'advanced-reports'
        ]
    }
};

// Pricing configuration
export const PRICING = {
    scout: 0,  // Free tier
    ally: 29,
    guide: 59,
    chief: 99,
    elder: 229
};

export const BILLING_MULTIPLIERS = {
    monthly: 1.20,
    quarterly: 1.10,
    annual: 1.00
};

// Order volume limits per tier (per billing cycle)
export const ORDER_LIMITS = {
    scout: { limit: 75, overageRate: 0, hardCap: true }, // Changed from 100 to 75, hard capped  // 100 orders, hard cap - no overage
    ally: { limit: 500, overageRate: 0.02 },      // 500 orders, 2% per order overage
    guide: { limit: 2000, overageRate: 0.01 },    // 2000 orders, 1% per order overage
    chief: { limit: 5000, overageRate: 0.005 },   // 5000 orders, 0.5% per order overage
    elder: { limit: Infinity, overageRate: 0 }    // Unlimited, no overage
};

// Menu limits per tier
export const MENU_LIMITS = {
    scout: { items: 10, reads: Infinity }, // Removed menu view limit (was 1000)  // Free tier limits
    ally: { items: Infinity, reads: Infinity },
    guide: { items: Infinity, reads: Infinity },
    chief: { items: Infinity, reads: Infinity },
    elder: { items: Infinity, reads: Infinity }
};

const SubscriptionContext = createContext();

export const useSubscription = () => {
    const context = useContext(SubscriptionContext);
    if (!context) {
        throw new Error('useSubscription must be used within a SubscriptionProvider');
    }
    return context;
};

export const SubscriptionProvider = ({ children }) => {
    const { currentUser, restaurantUid } = useAuth();
    const [subscription, setSubscription] = useState(null);
    const [serviceMode, setServiceMode] = useState('full_service');
    const [paymentTiming, setPaymentTiming] = useState('pre_pay');
    const [customerDisplayEnabled, setCustomerDisplayEnabled] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!restaurantUid) {
            setSubscription(null);
            setLoading(false);
            return;
        }

        // Listen to subscription changes in real-time
        const unsubscribe = onSnapshot(
            doc(db, 'restaurants', restaurantUid),
            (docSnap) => {
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setSubscription(data.subscription || null);
                    setServiceMode(data.serviceMode || 'full_service');
                    setPaymentTiming(data.paymentTiming || 'pre_pay');
                    setCustomerDisplayEnabled(!!data.customerDisplayEnabled);
                } else {
                    setSubscription(null);
                    setServiceMode('full_service');
                    setPaymentTiming('pre_pay');
                    setCustomerDisplayEnabled(false);
                }
                setLoading(false);
            },
            (error) => {
                console.error('Error fetching subscription:', error);
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [restaurantUid]);

    // Get current tier (default to 'ally' if no subscription)
    const getCurrentTier = () => {
        return subscription?.tier || 'ally';
    };

    // Check if user has access to a specific feature
    const hasFeatureAccess = (featureId) => {
        const tier = getCurrentTier();
        const tierInfo = TIER_FEATURES[tier];
        return tierInfo?.features?.includes(featureId) || false;
    };

    // Check if feature is in preview mode (visible but disabled)
    const isFeaturePreview = (featureId) => {
        const tier = getCurrentTier();
        const tierInfo = TIER_FEATURES[tier];
        return tierInfo?.previewFeatures?.includes(featureId) || false;
    };

    // Get tiers that have a specific feature
    const getFeatureTiers = (featureId) => {
        return Object.entries(TIER_FEATURES)
            .filter(([_, info]) => info.features?.includes(featureId))
            .map(([tier, _]) => tier);
    };

    // Get the minimum tier required for a feature
    const getMinimumTierForFeature = (featureId) => {
        const tiers = getFeatureTiers(featureId);
        if (tiers.length === 0) return 'elder';

        return tiers.reduce((min, tier) => {
            return TIER_FEATURES[tier].level < TIER_FEATURES[min].level ? tier : min;
        });
    };

    // Check if current tier can upgrade
    const canUpgrade = () => {
        const tier = getCurrentTier();
        return TIER_FEATURES[tier]?.level < 4;
    };

    // Get next available tier
    const getNextTier = () => {
        const tier = getCurrentTier();
        const currentLevel = TIER_FEATURES[tier]?.level || 1;

        return Object.entries(TIER_FEATURES)
            .find(([_, info]) => info.level === currentLevel + 1)?.[0] || null;
    };

    // Calculate price for a tier and billing cycle
    const calculatePrice = (tier, billingCycle = 'annual') => {
        const basePrice = PRICING[tier] || 0;
        const multiplier = BILLING_MULTIPLIERS[billingCycle] || 1;
        return (basePrice * multiplier).toFixed(2);
    };

    // Check subscription status (considers expiration and trial end)
    const isSubscriptionActive = () => {
        if (!subscription) return false;
        const status = subscription.status;

        // Explicitly cancelled or past_due
        if (status === 'cancelled' || status === 'past_due') return false;

        // Trial: check if trial has expired
        if (status === 'trialing') {
            if (subscription.trialEnd) {
                const trialEnd = subscription.trialEnd.toDate
                    ? subscription.trialEnd.toDate()
                    : new Date(subscription.trialEnd);
                if (new Date() > trialEnd) return false;
            }
            return true;
        }

        // Active: check if billing period has expired (grace period of 3 days)
        if (status === 'active') {
            if (subscription.currentPeriodEnd) {
                const periodEnd = subscription.currentPeriodEnd.toDate
                    ? subscription.currentPeriodEnd.toDate()
                    : new Date(subscription.currentPeriodEnd);
                const gracePeriod = new Date(periodEnd);
                gracePeriod.setDate(gracePeriod.getDate() + 3);
                if (new Date() > gracePeriod) return false;
            }
            return true;
        }

        // Scout (free) tier is always active
        if (subscription.tier === 'scout') return true;

        return false;
    };

    // Check if subscription is expired (past period end + grace)
    const isSubscriptionExpired = () => {
        if (!subscription) return false;
        if (subscription.tier === 'scout') return false;

        if (subscription.status === 'trialing' && subscription.trialEnd) {
            const trialEnd = subscription.trialEnd.toDate
                ? subscription.trialEnd.toDate()
                : new Date(subscription.trialEnd);
            return new Date() > trialEnd;
        }

        if (subscription.currentPeriodEnd) {
            const periodEnd = subscription.currentPeriodEnd.toDate
                ? subscription.currentPeriodEnd.toDate()
                : new Date(subscription.currentPeriodEnd);
            const gracePeriod = new Date(periodEnd);
            gracePeriod.setDate(gracePeriod.getDate() + 3);
            return new Date() > gracePeriod;
        }
        return false;
    };

    // Get days remaining in current period
    const getDaysRemaining = () => {
        if (!subscription?.currentPeriodEnd) return null;
        const periodEnd = subscription.currentPeriodEnd.toDate
            ? subscription.currentPeriodEnd.toDate()
            : new Date(subscription.currentPeriodEnd);
        const now = new Date();
        const diffDays = Math.ceil((periodEnd - now) / (1000 * 60 * 60 * 24));
        return Math.max(0, diffDays);
    };

    // Get days remaining in trial
    const getTrialDaysRemaining = () => {
        if (subscription?.status !== 'trialing' || !subscription?.trialEnd) return 0;

        const trialEnd = subscription.trialEnd.toDate ? subscription.trialEnd.toDate() : new Date(subscription.trialEnd);
        const now = new Date();
        const diffTime = trialEnd - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return Math.max(0, diffDays);
    };

    // Get order limit for current tier
    const getOrderLimit = () => {
        const tier = getCurrentTier();
        return ORDER_LIMITS[tier] || ORDER_LIMITS.ally;
    };

    // Calculate overage charge for an order amount
    const calculateOverageCharge = (orderTotal) => {
        const tier = getCurrentTier();
        const overageRate = ORDER_LIMITS[tier]?.overageRate || 0;
        return orderTotal * overageRate;
    };

    // Check if current usage is over limit
    const isOverOrderLimit = (currentCount) => {
        const { limit } = getOrderLimit();
        return currentCount > limit;
    };

    // Get menu limits for current tier
    const getMenuLimit = () => {
        const tier = getCurrentTier();
        return MENU_LIMITS[tier] || MENU_LIMITS.ally;
    };

    // Check if order is hard capped (no overage allowed)
    const isOrderHardCapped = () => {
        const tier = getCurrentTier();
        return ORDER_LIMITS[tier]?.hardCap === true;
    };

    // Service mode helpers
    const getServiceMode = () => serviceMode;

    const getPaymentTiming = () => paymentTiming;

    const isPayFirst = () => {
        if (serviceMode === 'counter_service' || serviceMode === 'food_truck') {
            return paymentTiming === 'pre_pay';
        }
        return false;
    };

    const requiresTables = () => {
        return serviceMode === 'full_service';
    };

    const hasServerRole = () => {
        return serviceMode === 'full_service';
    };

    const value = {
        subscription,
        loading,
        getCurrentTier,
        hasFeatureAccess,
        isFeaturePreview,
        getMinimumTierForFeature,
        canUpgrade,
        getNextTier,
        calculatePrice,
        isSubscriptionActive,
        isSubscriptionExpired,
        getDaysRemaining,
        getTrialDaysRemaining,
        TIER_FEATURES,
        PRICING,
        TRIAL_PERIOD_DAYS,
        ORDER_LIMITS,
        MENU_LIMITS,
        getOrderLimit,
        getMenuLimit,
        calculateOverageCharge,
        isOverOrderLimit,
        isOrderHardCapped,
        getServiceMode,
        getPaymentTiming,
        isPayFirst,
        requiresTables,
        hasServerRole,
        customerDisplayEnabled
    };

    return (
        <SubscriptionContext.Provider value={value}>
            {children}
        </SubscriptionContext.Provider>
    );
};
